import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';

// --- Replicating External Helpers & Constants ---
const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const isNumericHeader = (header) => {
    if (header.toUpperCase().includes('ACCOUNT') || header.toUpperCase().includes('CUSTOMER') || header.toUpperCase().includes('SALESPERSON') || header.toUpperCase().includes('GROUP') || header.toUpperCase().includes('BRAND')) {
        return false;
    }
    return ['SALES', 'RETURN', 'VAT', 'COMMISSION', 'NET', '%', 'AMT'].some(s => header.toUpperCase().includes(s));
};

const FOOTER_TOTAL_KEYS = [
    'NETCASH SALES', 
    'NETCREDIT SALES', 
    'NET SALERETURN', 
    'VAT AMT', 
    'NET SALES EXCLVAT'
];
// ------------------------------------------------

// Create styles for the PDF
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 10,
        fontSize: 8,
    },
    table: {
        display: 'flex',
        width: 'auto',
        flexDirection: 'column',
        // borderStyle: 'solid',
        // borderWidth: 0, 
        // borderRightWidth: 0, 
        // borderBottomWidth: 0, 
    },
    // Row/Header Borders (Top/Bottom) remain in the base row styles
    headerRow: {
        flexDirection: 'row',
        backgroundColor: '#e0f7fa',
        // borderBottomWidth: 1,
        // borderBottomColor: '#ccc',
        textAlign: 'center',
        fontWeight: 'bold',
    },
    row: {
        flexDirection: 'row',
        // borderBottomWidth: 1,
        // borderBottomColor: '#eee',
    },
    // Cell/HeaderCell/FooterCell now only define padding and font styles
    cell: {
        padding: 4,
        //borderRightColor: '#eee', // Keep color defined
    },
    headerCell: {
        padding: 4,
        fontWeight: 'bold',
        //borderRightColor: '#ccc', // Keep color defined
    },
    numeric: {
        textAlign: 'right',
    },
    left: {
        textAlign: 'left',
    },
    footerRow: {
        flexDirection: 'row',
        backgroundColor: '#80cbc4',
        // borderTopWidth: 2, 
        // borderTopColor: '#000',
        fontWeight: 'bold',
    },
    footerCell: {
        padding: 6,
        fontWeight: 'bold',
        fontSize: 10,
        //borderRightColor: '#000', // Keep color defined
    },
    descriptiveCell: {
        minWidth: '20%',
        maxWidth: '20%', 
        flexGrow: 1,
    }
});

const SalesTableGroupAggregatedPDF = ({ rows }) => {
    const descriptiveColumnKey = 'GROUP';

    const allKeys = rows.length > 0 ? Object.keys(rows[0]) : [];
    const displayKeys = allKeys.filter(key =>
        !['isGroupHeader', 'isSubHeader', 'isSubSubHeader', 'totalAmount', 'totalAmountExclVAT', descriptiveColumnKey].includes(key)
    );

    if (rows.length === 0) {
        return (
            <Document>
                <Page size="A4" style={styles.page} orientation="landscape">
                    <Text style={{ textAlign: 'center', marginTop: 50 }}>No sales data found for the selected filters.</Text>
                </Page>
            </Document>
        );
    }

    // Calculations remain the same
    const internalGrandTotals = (() => {
        const keysToTotal = displayKeys.filter(key => FOOTER_TOTAL_KEYS.includes(key));
        const totals = {};
        for (const key of keysToTotal) {
            totals[key] = rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
        }
        return totals;
    })();
    
    const totalValueKeys = displayKeys.filter(key => FOOTER_TOTAL_KEYS.includes(key));
    const descriptiveHeader = descriptiveColumnKey.replace(/([A-Z])/g, ' $1').trim().toUpperCase();

    const dynamicColumnWidth = `${(80 / displayKeys.length).toFixed(2)}%`;
    const descriptiveColumnWidth = '20%';

    let descriptiveSpanIndex = displayKeys.length;
    if (totalValueKeys.length > 0) {
        const firstTotalKey = totalValueKeys[0];
        descriptiveSpanIndex = displayKeys.indexOf(firstTotalKey);
    }
    
    const descriptiveFooterWidth = `${(20 + dynamicColumnWidth.replace('%', '') * descriptiveSpanIndex).toFixed(2)}%`;
    const remainingFooterWidth = dynamicColumnWidth;


    return (
        <Document>
            <Page size="A4" style={styles.page} orientation="landscape">
                <View style={styles.table}>
                    
                    {/* Table Head */}
                    <View style={styles.headerRow} fixed>
                        {/* 1. Descriptive Column Header (Always has right border) */}
                        <View style={[styles.headerCell, styles.descriptiveCell, { width: descriptiveColumnWidth }]}>
                            <Text>{descriptiveHeader}</Text>
                        </View>

                        {/* 2. Dynamic Headers (Explicitly set border on all) */}
                        {displayKeys.map((key, dataIndex) => {
                             const lastCell = dataIndex === displayKeys.length - 1;
                             return (
                                <View 
                                    key={key} 
                                    style={[
                                        styles.headerCell, 
                                        isNumericHeader(key) ? styles.numeric : styles.left, 
                                        { 
                                            width: dynamicColumnWidth,
                                            // borderRightWidth: lastCell ? 0 : 1, // Explicitly 0 or 1
                                            // borderRightColor: lastCell ? 'transparent' : styles.headerCell.borderRightColor,
                                        }
                                    ]}
                                >
                                    <Text>{key.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}</Text>
                                </View>
                            );
                        })}
                    </View>

                    {/* Table Body */}
                    {rows.map((row, index) => (
                        <View key={`agg-acc-row-${index}`} style={styles.row} wrap={false}>
                            {/* 1. Descriptive Column Cell (Always has right border) */}
                            <View style={[styles.cell, styles.left, styles.descriptiveCell, { width: descriptiveColumnWidth }]}>
                                <Text>{row[descriptiveColumnKey]}</Text>
                            </View>

                            {/* 2. Data Cells (Explicitly set border on all) */}
                            {displayKeys.map((key, dataIndex) => {
                                const isNumeric = isNumericHeader(key);
                                const lastCell = dataIndex === displayKeys.length - 1;

                                return (
                                    <View 
                                        key={key} 
                                        style={[
                                            styles.cell, 
                                            isNumeric ? styles.numeric : styles.left, 
                                            { 
                                                width: dynamicColumnWidth,
                                                // borderRightWidth: lastCell ? 0 : 1, // Explicitly 0 or 1
                                                // borderRightColor: lastCell ? 'transparent' : styles.cell.borderRightColor,
                                            }
                                        ]}
                                    >
                                        <Text>{isNumeric ? formatNumber(row[key]) : row[key]}</Text>
                                    </View>
                                );
                            })}
                        </View>
                    ))}

                    {/* Fixed Grand Totals Footer */}
                    {totalValueKeys.length > 0 && (
                        <View style={styles.footerRow} fixed>
                            {/* 1. Descriptive Span for the GRAND TOTAL label */}
                            <View 
                                style={[
                                    styles.footerCell, 
                                    styles.numeric, 
                                    { 
                                        width: descriptiveFooterWidth, 
                                       // borderRightWidth: 0 // Explicitly 0
                                    }
                                ]}
                            >
                                <Text>GRAND TOTAL:</Text>
                            </View>
                            
                            {/* 2. Data cells where the totals begin (Explicitly set border on all) */}
                            {displayKeys.slice(descriptiveSpanIndex).map((key, index) => {
                                const isTotalKey = FOOTER_TOTAL_KEYS.includes(key);
                                const lastCell = index === displayKeys.slice(descriptiveSpanIndex).length - 1;
                                
                                return (
                                    <View 
                                        key={key} 
                                        style={[
                                            styles.footerCell, 
                                            styles.numeric, 
                                            { 
                                                width: remainingFooterWidth, 
                                                // borderRightWidth: lastCell ? 0 : 1, // Explicitly 0 or 1
                                                // borderRightColor: styles.footerCell.borderRightColor,
                                            }
                                        ]}
                                    >
                                        <Text>{isTotalKey ? formatNumber(internalGrandTotals[key]) : ''}</Text>
                                    </View>
                                );
                            })}
                        </View>
                    )}
                </View>
            </Page>
        </Document>
    );
};

export default SalesTableGroupAggregatedPDF;